<template>

  <v-dialog :value="value" :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="$vuetify.breakpoint.sm ? '80vw' : $vuetify.breakpoint.md ? '70vw' : '50vw'" @input="close" @keydown.stop.esc="close"
    :content-class="'app-dialog ' + (isMobileOnly ? 'mobile' : '')" persistent :retain-focus="false">

    <v-card>

      <v-toolbar class="app-dialog-header">
        <v-toolbar-title>{{$t('myorganisations.selectMyOrganisation')}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="close">
          <v-icon small>fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="app-dialog-body">

        <dx-data-grid ref="myOrgGrid" :data-source="myOrganisations" :show-borders="false" :showRowLines="true" :showColumnLines="false"
          :hoverStateEnabled="true" key-expr="code" :column-hiding-enabled="$vuetify.breakpoint.xsOnly" :class="dxGridCssClasses"
          :height="calculatedGridHeight" :onRowClick="onSelected">

          <dx-column data-field="edrpo" :caption="edrpoColumnCaption" />
          <dx-column data-field="fullName" :caption="fullNameColumnCaption" />
          <dx-column data-field="email" :caption="emailColumnCaption" />
          <dx-column data-field="phone" :caption="phoneColumnCaption" />

          <dx-load-panel :enabled="false" />

          <dx-scrolling mode="virtual" />

          <dx-sorting mode="single" />

          <dx-filter-panel :visible="true" :texts="dxDataGridLocalizationDefaults.filterPanelTexts" />

          <dx-filter-row :visible="true" applyFilter="auto" :applyFilterText="dxDataGridLocalizationDefaults.filterRow.applyFilterText"
            :betweenEndText="dxDataGridLocalizationDefaults.filterRow.betweenEndText"
            :betweenStartText="dxDataGridLocalizationDefaults.filterRow.betweenStartText"
            :resetOperationText="dxDataGridLocalizationDefaults.filterRow.resetOperationText"
            :operationDescriptions="dxDataGridLocalizationDefaults.filterRow.operationDescriptions" />

          <dx-filter-builder :filterOperationDescriptions="dxDataGridLocalizationDefaults.filterBuilderOperationDescriptions"
            :groupOperationDescriptions="dxDataGridLocalizationDefaults.filterBuilderGroupOperationDescriptions" />

          <dx-filter-builder-popup :title="$t('documentsview.gridLocale.filter-builder-popup.title')" />

          <dx-header-filter :allowSearch="true" :visible="true" :width="dxGridHeaderFilterWidth" :height="dxGridHeaderFilterHeight"
            :texts="{cancel: dxGridHeaderFilterCancelBtnLocale, emptyValue: '', ok: dxGridHeaderFilterOkBtnLocale}" />

          <dx-search-panel :visible="false" />

        </dx-data-grid>

      </v-card-text>

    </v-card>
  </v-dialog>
</template>

<script>
import { getLocale, logHelper } from '@/helpers/';
import uiMixin from '@/mixins/ui-mixin';
import { actions as _act } from '@/models/store.actions';
import { organizationService } from '@/services/organization.service';

import {
  DxColumn,
  DxDataGrid,
  DxFilterBuilder,
  DxFilterBuilderPopup,
  DxFilterPanel,
  DxFilterRow,
  DxHeaderFilter,
  DxLoadPanel,
  DxScrolling,
  DxSearchPanel,
  DxSorting
} from 'devextreme-vue/data-grid';
import ukMessages from '@/locales/dx-uk.json';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale, loadMessages } from 'devextreme/localization';
import { DxDataGridLocalizationDefaults } from '@/models/defaults';
import { AppError } from '@/models/log.models';

export default {
  name: 'SelectMyOrganisationDlg',
  components: {
    DxColumn,
    DxDataGrid,
    DxFilterBuilder,
    DxFilterBuilderPopup,
    DxFilterPanel,
    DxFilterRow,
    DxHeaderFilter,
    DxLoadPanel,
    DxScrolling,
    DxSearchPanel,
    DxSorting
  },
  mixins: [uiMixin],
  inject: ['isMobileOnly'],
  props: {
    value: { default: false }
  },
  data() {
    return {
      myOrganisations: [],
      calculatedGridHeight: null,
      dxDataGridLocalizationDefaults: null
    };
  },
  computed: {
    fullNameColumnCaption() {
      return getLocale(`myorganisations.grid.fullName.title`);
    },
    phoneColumnCaption() {
      return getLocale(`myorganisations.grid.phone.title`);
    },
    emailColumnCaption() {
      return getLocale(`myorganisations.grid.email.title`);
    },
    edrpoColumnCaption() {
      return getLocale(`myorganisations.grid.edrpo.title`);
    },
    dxGridHeaderFilterWidth() {
      return this.$vuetify.breakpoint.xsOnly ? undefined : 400;
    },
    dxGridHeaderFilterHeight() {
      return this.$vuetify.breakpoint.xsOnly ? undefined : 400;
    },
    dxGridHeaderFilterOkBtnLocale() {
      return getLocale('buttons.ok');
    },
    dxGridHeaderFilterCancelBtnLocale() {
      return getLocale('buttons.cancel');
    },
    dxGridCssClasses() {
      let css = 'app-dx-data-grid';
      if (this.$vuetify.breakpoint.xsOnly) {
        css += ` app-dx-data-grid-mobile`;
      }
      return css;
    }
  },
  created() {
    this.dialogs = new Set();
    this.dialogs.add('SelectMyOrganisationDlg');
    this.initDxLocalization();
    this.calculatedGridHeight = this.$vuetify.breakpoint.xsOnly
      ? '80vh'
      : '50vh';
  },
  mounted() {
    this.subscribeOnEvents();
    this.loadMyOrgs();
  },
  destroyed() {
    this.unsubscribeFromEvents();
  },
  methods: {
    close() {
      let lastValue = Array.from(this.dialogs).pop();
      if (lastValue === 'SelectMyOrganisationDlg') {
        this.$emit('input');
      }
    },
    onSelected({ data }) {
      this.$emit('myOrgSelected', data);
    },
    changeDxLocalization() {
      locale(this.$store.getters[_act.SETT_GET_LOCALEGET]);
      this.dxDataGridLocalizationDefaults =
        new DxDataGridLocalizationDefaults();
    },
    initDxLocalization() {
      loadMessages(ukMessages);
      loadMessages(ruMessages);
      this.changeDxLocalization();
    },
    async loadMyOrgs() {
      let loader = this.showLoading();
      try {
        this.myOrganisations = await organizationService.getMyOrgs();
      } catch (err) {
        if (err instanceof AppError === false) {
          logHelper.error(err);
        }
      } finally {
        loader.hide();
      }
    },
    subscribeOnEvents() {
      this.$eventBus.$on('locale-changed', this.changeDxLocalization);
    },
    unsubscribeFromEvents() {
      this.$eventBus.$off('locale-changed', this.changeDxLocalization);
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/dx-data-grid.scss';
</style>
